import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileIcon,
  FolderIcon,
} from 'lucide-react';
import { type JSX, useMemo, useState } from 'react';

import { cn } from '@/lib/utils.js';

import { formatBytes } from './artifact-display.js';
import {
  buildZipTree,
  type ZipListing,
  type ZipTopLevelRow,
  type ZipTreeNode,
} from './zip-listing.js';

/** The most rows the full tree draws. Every row is real DOM, and a huge archive is read in the download. */
export const ZIP_TREE_MAX_ROWS = 5000;

/** Top-level rows a tile has room for. */
export const ZIP_TILE_ROWS = 6;

/** A node on screen, with whatever of an open folder's contents is on screen too. */
interface ShownNode {
  node: ZipTreeNode;
  children: ShownNode[];
}

/** How many rows the open tree has: every top-level node, and the children of each open folder. */
function visibleRowCount(
  nodes: readonly ZipTreeNode[],
  open: ReadonlySet<string>,
): number {
  let count = 0;
  for (const node of nodes) {
    count += 1;
    if (node.isFolder && open.has(node.path)) {
      count += visibleRowCount(node.children, open);
    }
  }
  return count;
}

/**
 * The open tree as it is drawn, nested the way the archive nests it and cut after `limit` rows in tree
 * order. The nesting is what tells a screen reader which files sit inside which folder.
 */
function shownTree(
  nodes: readonly ZipTreeNode[],
  open: ReadonlySet<string>,
  limit: number,
): ShownNode[] {
  let rowsLeft = limit;
  const shownLevel = (level: readonly ZipTreeNode[]): ShownNode[] => {
    const shown: ShownNode[] = [];
    for (const node of level) {
      if (rowsLeft === 0) break;
      rowsLeft -= 1;
      const isOpen = node.isFolder && open.has(node.path);
      shown.push({ node, children: isOpen ? shownLevel(node.children) : [] });
    }
    return shown;
  };
  return shownLevel(nodes);
}

/** "N files · size" for the whole archive, or "At least …" when the listing stopped before its end. */
function summaryOf(listing: ZipListing): string {
  const files = listing.entries.filter((entry) => !entry.isFolder);
  const total = files.reduce((sum, entry) => sum + entry.size, 0);
  const noun = files.length === 1 ? 'file' : 'files';
  const summary = `${files.length.toLocaleString('en-US')} ${noun} · ${formatBytes(total)}`;
  return listing.complete ? summary : `At least ${summary}`;
}

/** A line under the tree saying the archive holds more than is drawn. */
function PartialNote({ children }: { children: string }): JSX.Element {
  return (
    <p className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * A zip tile: the archive's top level, folders first, each folder with how much it holds. It draws the
 * summary {@link zipTopLevel} made, which is what the list caches, never the archive itself.
 */
export function ZipTileView({
  rows,
}: {
  rows: readonly ZipTopLevelRow[];
}): JSX.Element {
  return (
    <ul className="flex h-full w-full flex-col gap-0.5 overflow-hidden p-2 text-left text-[9px] leading-tight text-muted-foreground">
      {rows.map((row) => (
        <li
          key={`${row.isFolder ? 'folder' : 'file'}:${row.name}`}
          className="flex min-w-0 items-center gap-1"
        >
          {row.isFolder ? (
            <FolderIcon className="size-3 shrink-0" aria-hidden />
          ) : (
            <FileIcon className="size-3 shrink-0" aria-hidden />
          )}
          <span className="truncate text-foreground">{row.name}</span>
          {row.isFolder && (
            <span className="ml-auto shrink-0">
              {`${row.itemCount} ${row.itemCount === 1 ? 'item' : 'items'}`}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** The visible line of one node. A folder is a button that opens and closes it; a file shows its size. */
function TreeRow({
  node,
  depth,
  expanded,
  onToggle,
}: {
  node: ZipTreeNode;
  depth: number;
  expanded: boolean;
  onToggle: (path: string) => void;
}): JSX.Element {
  const indent = { paddingLeft: `${depth * 1.25 + 0.5}rem` };
  if (node.isFolder) {
    return (
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => onToggle(node.path)}
        style={indent}
        className="flex w-full items-center gap-1.5 rounded py-1 pr-2 text-left hover:bg-muted"
      >
        {expanded ? (
          <ChevronDownIcon className="size-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRightIcon className="size-3.5 shrink-0" aria-hidden />
        )}
        <FolderIcon
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="truncate">{node.name}</span>
      </button>
    );
  }
  return (
    <div
      style={indent}
      className="flex items-center gap-1.5 py-1 pr-2 pl-2 text-left"
    >
      <span className="size-3.5 shrink-0" aria-hidden />
      <FileIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate">{node.name}</span>
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
        {formatBytes(node.size)}
      </span>
    </div>
  );
}

/** One level of the tree as a list. An open folder's own list sits inside the folder's item. */
function TreeLevel({
  shown,
  depth,
  open,
  onToggle,
}: {
  shown: readonly ShownNode[];
  depth: number;
  open: ReadonlySet<string>;
  onToggle: (path: string) => void;
}): JSX.Element {
  return (
    <ul className={depth === 0 ? 'p-1' : undefined}>
      {shown.map(({ node, children }) => (
        <li key={node.path + (node.isFolder ? '/' : '')}>
          <TreeRow
            node={node}
            depth={depth}
            expanded={open.has(node.path)}
            onToggle={onToggle}
          />
          {children.length > 0 && (
            <TreeLevel
              shown={children}
              depth={depth + 1}
              open={open}
              onToggle={onToggle}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The files inside a zip archive, listed from its directory and never extracted: a tree of nested lists whose
 * folders open on click, with each file's size. Like the table preview, it draws at most
 * {@link ZIP_TREE_MAX_ROWS} rows and says when it stopped, and it says so too when the listing itself stopped
 * at one of its limits.
 */
export function ZipTreeView({
  listing,
  scrollBox = 'max-h-[70vh] overflow-auto',
}: {
  listing: ZipListing;
  /** The classes of the one scroll container, as the other text-shaped previews take them. */
  scrollBox?: string;
}): JSX.Element {
  const tree = useMemo(() => buildZipTree(listing.entries), [listing]);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  if (tree.length === 0 && listing.complete) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        This archive is empty.
      </p>
    );
  }
  const shown = shownTree(tree, open, ZIP_TREE_MAX_ROWS);
  const toggle = (path: string): void =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(path)) next.add(path);
      return next;
    });
  return (
    <div className={cn(scrollBox, 'rounded-lg border border-border text-sm')}>
      <p className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
        {summaryOf(listing)}
      </p>
      <TreeLevel shown={shown} depth={0} open={open} onToggle={toggle} />
      {visibleRowCount(tree, open) > ZIP_TREE_MAX_ROWS && (
        <PartialNote>
          {`Showing the first ${ZIP_TREE_MAX_ROWS.toLocaleString('en-US')} rows. Download the archive to see all of it.`}
        </PartialNote>
      )}
      {!listing.complete && (
        <PartialNote>
          Only the first part of this archive is listed. Download the archive to
          see all of it.
        </PartialNote>
      )}
    </div>
  );
}
